import React, { FC, useCallback, useMemo } from 'react';

import { EvSection } from '@evinced-private/ui-common';

import { parseTimeStr } from '../../helpers/SchedulingHelper';
import { getClientTimezoneValue } from '../../helpers/timezone/TimezoneHelper';
import {
	FrequencyDto,
	IScheduledJobConfiguration,
	JobTypeDto
} from '../../interfaces/ScheduledJobConfiguration';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';

import { SchedulingSectionContextWrapper } from './context/SchedulingSectionContextWrapper';
import { EmailSettings } from './EmailSettings';
import { getRoundedUpTime } from './helpers';
import { SchedulingSectionProps } from './SchedulingSection.types';
import { SectionHeader } from './SectionHeader';
import { SectionMainBlockWithContext } from './SectionMainBlockWithContext';

import './SchedulingSection.scss';

export const SchedulingSection: FC<SchedulingSectionProps> = (props) => {
	const { initialConfig, onConfigChange: onConfigChangeProps, error } = props;
	const { getToggle } = useConfiguration();
	const showEmailNotifications = getToggle(SiteScannerUiToggles.ENABLE_EMAIL_NOTIFICATIONS);

	const onConfigChange = useCallback(
		(config: IScheduledJobConfiguration) => {
			const { active } = config;
			let newConfig: IScheduledJobConfiguration = { ...config };
			if (!active) {
				newConfig = { ...initialConfig, active };
			}
			onConfigChangeProps(newConfig);
		},
		[onConfigChangeProps, initialConfig]
	);

	const { active, frequency, interval, jobType, timezone, hours, minutes, ...rest } =
		initialConfig || {};

	const configWithDefaults = useMemo(() => {
		const defaultTime = getRoundedUpTime();
		const { hours: defaultHours, minutes: defaultMinutes } = parseTimeStr(
			defaultTime.value as string
		);

		return {
			active: active || false,
			frequency: frequency || FrequencyDto.WEEKLY,
			interval: interval || 1,
			jobType: jobType || JobTypeDto.CRAWL_AND_SCAN,
			timezone: timezone || getClientTimezoneValue(),
			hours: hours || [defaultHours],
			minutes: minutes || [defaultMinutes],
			...rest
		};
	}, [active, frequency, interval, jobType, timezone, hours, minutes, rest]);

	return (
		<EvSection>
			<div className="scheduling-section-wrapper">
				<fieldset
					className="scheduling-section-fieldset"
					aria-labelledby="scheduling-section-title"
				>
					<SchedulingSectionContextWrapper
						initialConfig={configWithDefaults}
						onConfigChange={onConfigChange}
						error={error}
					>
						<SectionHeader />
						<SectionMainBlockWithContext />
					</SchedulingSectionContextWrapper>
				</fieldset>
				{showEmailNotifications && <EmailSettings />}
			</div>
		</EvSection>
	);
};
